'use client';

import { useState, useEffect, useRef } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent } from '@/lib/tools/types';
import { Palette, Copy, Check, RotateCcw, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { logToolUsage } from '@/lib/tools/analytics';
import { ToolSignupWall } from './tool-signup-wall';
import {
  ETSY_LISTING_FEE,
  ETSY_TRANSACTION_FEE_RATE,
  ETSY_PAYMENT_PROCESSING_RATE,
  ETSY_PAYMENT_PROCESSING_FIXED,
  ETSY_OFFSITE_ADS_RATE_UNDER_10K,
  ETSY_OFFSITE_ADS_RATE_OVER_10K,
  ETSY_REGULATORY_FEE_RATE,
  ETSY_DATA_UPDATED,
} from '@/lib/tools/marketplace-data';

interface EtsyCalculatorProps {
  locale: Locale;
  content: ToolContent;
}

export function EtsyCalculator({ locale, content }: EtsyCalculatorProps) {
  const [sellingPrice, setSellingPrice] = useState('');
  const [shippingCharge, setShippingCharge] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [monthlyVolume, setMonthlyVolume] = useState('50');
  const [offsiteAds, setOffsiteAds] = useState(false);
  const [revenueAbove10k, setRevenueAbove10k] = useState(false);
  const [includeShippingInFees, setIncludeShippingInFees] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const hasLoggedRef = useRef(false);

  const labels = content.labels;

  useEffect(() => {
    const sell = parseFloat(sellingPrice.replace(',', '.'));
    if (!hasLoggedRef.current && !isNaN(sell) && sell > 0) {
      hasLoggedRef.current = true;
      logToolUsage({ tool_name: 'etsy-calculator', locale });
    }
  }, [sellingPrice, locale]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const copyToClipboard = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const calculateResults = () => {
    const selling = parseFloat(sellingPrice.replace(',', '.')) || 0;
    const shipping = parseFloat(shippingCharge.replace(',', '.')) || 0;
    const purchasing = parseFloat(purchasePrice.replace(',', '.')) || 0;
    const volume = parseInt(monthlyVolume) || 0;

    if (selling <= 0) return null;

    const orderTotal = selling + shipping;
    const feeBase = includeShippingInFees ? orderTotal : selling;

    const listingFee = ETSY_LISTING_FEE;
    const transactionFee = feeBase * ETSY_TRANSACTION_FEE_RATE;
    const paymentProcessingFee = orderTotal * ETSY_PAYMENT_PROCESSING_RATE + ETSY_PAYMENT_PROCESSING_FIXED;
    const regulatoryFee = feeBase * ETSY_REGULATORY_FEE_RATE;
    const offsiteAdsFee = offsiteAds
      ? orderTotal * (revenueAbove10k ? ETSY_OFFSITE_ADS_RATE_OVER_10K : ETSY_OFFSITE_ADS_RATE_UNDER_10K)
      : 0;

    const totalFeesPerUnit = listingFee + transactionFee + paymentProcessingFee + regulatoryFee + offsiteAdsFee;
    const profitPerUnit = selling - purchasing - totalFeesPerUnit;
    const margin = (profitPerUnit / selling) * 100;

    return {
      listingFee,
      transactionFee,
      paymentProcessingFee,
      regulatoryFee,
      offsiteAdsFee,
      totalFeesPerUnit,
      profitPerUnit,
      margin,
      monthlyFees: totalFeesPerUnit * volume,
      monthlyProfit: profitPerUnit * volume,
      yearlyProfit: profitPerUnit * volume * 12,
    };
  };

  const result = calculateResults();

  const reset = () => {
    setSellingPrice('');
    setShippingCharge('');
    setPurchasePrice('');
    setMonthlyVolume('50');
    setOffsiteAds(false);
    setRevenueAbove10k(false);
    setIncludeShippingInFees(true);
  };

  return (
    <div className="mx-auto max-w-2xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100">
          <Palette className="h-7 w-7 text-orange-600" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{content.title}</h2>
        <p className="mt-3 text-lg text-slate-600">{content.description}</p>
      </div>

      {/* Calculator Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* Selling Price */}
        <div className="mb-6">
          <label htmlFor="etsySellingPrice" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.sellingPrice || 'Verkoopprijs'}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
            <input
              type="text"
              id="etsySellingPrice"
              value={sellingPrice}
              onChange={(e) => setSellingPrice(e.target.value)}
              placeholder="0,00"
              className="block w-full rounded-xl border border-slate-300 py-3 pl-8 pr-4 text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        {/* Shipping Charge */}
        <div className="mb-6">
          <label htmlFor="etsyShipping" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.shippingCharge || 'Verzendkosten (aan klant)'}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
            <input
              type="text"
              id="etsyShipping"
              value={shippingCharge}
              onChange={(e) => setShippingCharge(e.target.value)}
              placeholder="0,00"
              className="block w-full rounded-xl border border-slate-300 py-3 pl-8 pr-4 text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        {/* Purchase Price */}
        <div className="mb-6">
          <label htmlFor="etsyPurchasePrice" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.purchasePrice || 'Inkoopprijs'}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
            <input
              type="text"
              id="etsyPurchasePrice"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              placeholder="0,00"
              className="block w-full rounded-xl border border-slate-300 py-3 pl-8 pr-4 text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        {/* Monthly Volume */}
        <div className="mb-6">
          <label htmlFor="etsyVolume" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.monthlyVolume || 'Maandelijks volume'}
          </label>
          <input
            type="number"
            id="etsyVolume"
            value={monthlyVolume}
            onChange={(e) => setMonthlyVolume(e.target.value)}
            min="0"
            placeholder="50"
            className="block w-full rounded-xl border border-slate-300 py-3 px-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        {/* Toggles */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
            <span className="text-sm font-medium text-slate-700">{labels.offsiteAds || 'Offsite Ads'}</span>
            <button
              type="button"
              onClick={() => setOffsiteAds(!offsiteAds)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${offsiteAds ? 'bg-brand-600' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${offsiteAds ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {offsiteAds && (
            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-sm font-medium text-slate-700">{labels.revenueAbove10k || 'Jaaromzet > $10.000 (12% i.p.v. 15%)'}</span>
              <button
                type="button"
                onClick={() => setRevenueAbove10k(!revenueAbove10k)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${revenueAbove10k ? 'bg-brand-600' : 'bg-slate-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${revenueAbove10k ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        {result && (
          <div className="mt-6 space-y-3 rounded-xl bg-slate-50 p-4">
            <ResultRow label={`${labels.listingFee || 'Listing fee'} ($${ETSY_LISTING_FEE})`} value={formatCurrency(result.listingFee)} copyKey="listing" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <ResultRow label={`${labels.transactionFee || 'Transaction fee'} (${(ETSY_TRANSACTION_FEE_RATE * 100).toFixed(1)}%)`} value={formatCurrency(result.transactionFee)} copyKey="transaction" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <ResultRow label={`${labels.paymentFee || 'Payment processing'} (${(ETSY_PAYMENT_PROCESSING_RATE * 100).toFixed(0)}% + €${ETSY_PAYMENT_PROCESSING_FIXED.toFixed(2)})`} value={formatCurrency(result.paymentProcessingFee)} copyKey="payment" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <ResultRow label={`${labels.regulatoryFee || 'Regulatory fee'} (${(ETSY_REGULATORY_FEE_RATE * 100).toFixed(2)}%)`} value={formatCurrency(result.regulatoryFee)} copyKey="regulatory" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            {result.offsiteAdsFee > 0 && (
              <ResultRow label={labels.offsiteAdsFee || 'Offsite Ads fee'} value={formatCurrency(result.offsiteAdsFee)} copyKey="offsiteAds" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            )}
            <div className="border-t border-slate-200 pt-3">
              <ResultRow label={labels.totalFeesPerUnit || 'Totale kosten per verkoop'} value={formatCurrency(result.totalFeesPerUnit)} copyKey="totalFees" copied={copied} onCopy={copyToClipboard} bold copyLabel={labels.copyResult} />
            </div>
            <div className="border-t border-slate-200 pt-3">
              <ResultRow
                label={labels.profitPerUnit || 'Winst per verkoop'}
                value={formatCurrency(result.profitPerUnit)}
                copyKey="profit"
                copied={copied}
                onCopy={copyToClipboard}
                bold
                valueColor={result.profitPerUnit >= 0 ? 'text-green-600' : 'text-red-600'}
                copyLabel={labels.copyResult}
              />
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-slate-500">{labels.margin || 'Marge'}</span>
                <span className={`text-sm font-medium ${result.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {result.margin.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3 space-y-2">
              <ResultRow label={labels.monthlyFees || 'Maandelijkse Etsy kosten'} value={formatCurrency(result.monthlyFees)} copyKey="monthlyFees" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
              <ResultRow label={labels.monthlyProfit || 'Maandelijkse winst'} value={formatCurrency(result.monthlyProfit)} copyKey="monthlyProfit" copied={copied} onCopy={copyToClipboard} valueColor={result.monthlyProfit >= 0 ? 'text-green-600' : 'text-red-600'} copyLabel={labels.copyResult} />
              <ResultRow label={labels.yearlyProfit || 'Jaarlijkse winst'} value={formatCurrency(result.yearlyProfit)} copyKey="yearlyProfit" copied={copied} onCopy={copyToClipboard} bold valueColor={result.yearlyProfit >= 0 ? 'text-green-600' : 'text-red-600'} copyLabel={labels.copyResult} />
            </div>
          </div>
        )}

        {/* Reset Button */}
        <button
          type="button"
          onClick={reset}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="h-4 w-4" />
          {labels.reset || 'Reset'}
        </button>
      </div>

      {/* Data Updated Notice */}
      <div className="mt-3 text-center text-xs text-slate-400">
        {labels.dataUpdated || 'Tarieven bijgewerkt'}: {ETSY_DATA_UPDATED}
      </div>

      {/* Fee Overview Table */}
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{labels.feeOverview || 'Etsy fee overzicht'}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="py-2 pr-4 text-left font-medium text-slate-600">{labels.feeType || 'Fee type'}</th>
                <th className="py-2 pl-4 text-right font-medium text-slate-600">{labels.rate || 'Tarief'}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-4 text-slate-700">Listing fee</td>
                <td className="py-2 pl-4 text-right font-medium text-slate-900">${ETSY_LISTING_FEE.toFixed(2)}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-4 text-slate-700">Transaction fee</td>
                <td className="py-2 pl-4 text-right font-medium text-slate-900">{(ETSY_TRANSACTION_FEE_RATE * 100).toFixed(1)}%</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-4 text-slate-700">Payment processing</td>
                <td className="py-2 pl-4 text-right font-medium text-slate-900">{(ETSY_PAYMENT_PROCESSING_RATE * 100).toFixed(0)}% + €{ETSY_PAYMENT_PROCESSING_FIXED.toFixed(2)}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-4 text-slate-700">Regulatory operating fee</td>
                <td className="py-2 pl-4 text-right font-medium text-slate-900">{(ETSY_REGULATORY_FEE_RATE * 100).toFixed(2)}%</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-4 text-slate-700">Offsite Ads (&lt;$10K)</td>
                <td className="py-2 pl-4 text-right font-medium text-slate-900">{(ETSY_OFFSITE_ADS_RATE_UNDER_10K * 100).toFixed(0)}%</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-slate-700">Offsite Ads (&gt;$10K)</td>
                <td className="py-2 pl-4 text-right font-medium text-slate-900">{(ETSY_OFFSITE_ADS_RATE_OVER_10K * 100).toFixed(0)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      <ToolSignupWall locale={locale} />

      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition-all hover:bg-brand-50"
        >
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {content.instructions && content.instructions.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            {locale === 'nl' ? 'Hoe werkt het?' : locale === 'de' ? 'Wie funktioniert es?' : locale === 'fr' ? 'Comment ça marche?' : 'How does it work?'}
          </h3>
          <ol className="space-y-2">
            {content.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">{index + 1}</span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">{content.disclaimer.heading}</h3>
          <p className="text-xs text-slate-500 leading-relaxed">{content.disclaimer.content}</p>
        </div>
      )}
    </div>
  );
}

function ResultRow({ label, value, copyKey, copied, onCopy, bold, valueColor, copyLabel }: {
  label: string;
  value: string;
  copyKey: string;
  copied: string | null;
  onCopy: (value: string, key: string) => void;
  bold?: boolean;
  valueColor?: string;
  copyLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${bold ? 'font-medium text-slate-700' : 'text-slate-600'}`}>{label}</span>
      <div className="flex items-center gap-2">
        <span className={`${bold ? 'text-lg font-semibold' : 'font-medium'} ${valueColor || 'text-slate-900'}`}>{value}</span>
        <button
          onClick={() => onCopy(value.replace(/[^0-9,.-]/g, ''), copyKey)}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
          title={copyLabel || 'Copy'}
        >
          {copied === copyKey ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
